import {useParams} from 'react-router-dom'
import cn from 'clsx'
import {parseISO, getMonth, getYear} from 'date-fns'
import {Space} from 'antd'

import styles from './UserProjectScheduleList.module.scss'
import {
  Box,
  Text,
  Block,
  ListHeader,
  Circle,
  CentsMoney,
  MoneyBanknotesRotatedSecondIcon,
  RotateArrowThirdIcon,
  RotateArrowFourthIcon,
} from 'common/components'
import {
  _differenceInCalendarDays,
  formatDate,
  formatMoney,
  isItemInDrawdownColumn,
  maybePluralize,
  // totalNumberFromArray,
} from 'common/helpers'
import {
  useGetProject,
  useGetProjectTransactions,
  isScheduleItemRepayment,
  TRANSACTION_NAME,
  Transaction,
} from '..'

interface ScheduleMonthProps {
  monthSchedule?: MonthSchedule
}

interface ScheduleItemProps {
  item: any
}

interface MonthSchedule {
  month: number
  year: number
  items: any[]
}

interface BarProps {
  project: any
  transactions: any[]
}

export default function UserProjectScheduleList() {
  const params = useParams()
  const {data: project} = useGetProject(params.uuid)
  const {data: transactions} = useGetProjectTransactions(params.uuid)

  if (!transactions.length) return null

  return (
    <Box mt={40}>
      <Head />
      <ul className={styles.list}>
        {getMonthSchedules(transactions).map((monthSchedule) => (
          <ScheduleMonth
            key={`${monthSchedule.month}${monthSchedule.year}`}
            monthSchedule={monthSchedule}
          />
        ))}
      </ul>
      <Bar project={project} transactions={transactions} />
    </Box>
  )
}

const Head = () => (
  <ListHeader className={styles.grid}>
    <Box align="right">Date</Box>
    <div>Reference</div>
    <Box align="right">Interest, fees</Box>
    <Box align="right">Drawdowns</Box>
    <Box align="right">Repayments</Box>
    <Box align="right">Current balance</Box>
  </ListHeader>
)

const ScheduleMonth = ({monthSchedule}: ScheduleMonthProps) => (
  <Block as="li" className={styles.month} type="grey">
    {monthSchedule.items.map((item, index) => (
      <ScheduleItem key={index} item={item} />
    ))}
  </Block>
)

const ScheduleItem = ({item}: ScheduleItemProps) => {
  const isDrawdown = isItemInDrawdownColumn(item)
  const isRepayment = isScheduleItemRepayment(item)

  return (
    <div className={styles.grid}>
      <div className={styles.date}>
        <Circle color={isRepayment ? 'darkGreen' : undefined} />
        <p>{formatDate(item.date, 'fifth')}</p>
      </div>
      <div className={styles.reference}>
        <Space>
          {isDrawdown ? (
            <RotateArrowThirdIcon />
          ) : isRepayment ? (
            <RotateArrowFourthIcon />
          ) : null}
          <p className={styles.type}>
            { !item.transaction && !item.xeroStatement && (isDrawdown || isRepayment) && 'Est. '}
            {TRANSACTION_NAME[item.type as Transaction]}
          </p>
        </Space>
        {item.notes && (
          <Text size="xs" color="grey" ml={12}>
            {item.notes}
          </Text>
        )}
      </div>
      <Text align="right">
        {!isDrawdown &&
          !isRepayment &&
          formatMoney(item.amount, {
            prefix: false,
            cents: true,
          })}
      </Text>
      <Text align="right">
        {isDrawdown &&
          formatMoney(item.amount, {
            prefix: false,
            cents: true,
          })}
      </Text>
      <Text align="right" color="greenDeep">
        {isRepayment &&
          formatMoney(Math.abs(item.amount), {
            prefix: false,
            cents: true,
            parentheses: true,
          })}
      </Text>
      <Box align="right" size="lg" weight={isRepayment ? 'bold' : 'medium'}>
        <CentsMoney
          as="span"
          value={Math.abs(item.balanceAfter)}
          parentheses={item.balanceAfter < 0}
        />
      </Box>
    </div>
  )
}

const Bar = ({project, transactions}: BarProps) => {
  const today = new Date()
  const totalDays = _differenceInCalendarDays(today, parseISO(project.openDate));

  // const totalInterestFees = totalNumberFromArray(
  //   transactions.filter(
  //     (transaction) =>
  //       transaction.type !== 'drawdown' &&
  //       transaction.type !== 'repayment' &&
  //       transaction.type !== 'application-fee'
  //   ),
  //   'amount'
  // )
  const totalInterestFees = project.currentIncome || 0
  const totalWithdrawn    = project.withdrawn
  const totalRepaid       = project.repaid
  const totalBalance      = project.balance

  // console.log(project)
  
  return (
    <>
      <Block className={cn(styles.grid, styles.grid_bar)} type="black">
        <div className={styles.barColumn}>
          <div>
            <p className={styles.label}>
              {`current total, ${formatDate(today, 'third')}`}
            </p>
            <Text size="xl">{maybePluralize(totalDays, 'day')}</Text>
          </div>
        </div>
        <Box align="right">
          <p className={styles.label}>interest & fees</p>
          <CentsMoney value={totalInterestFees} />
        </Box>
        <Box align="right">
          <p className={styles.label}>withdrawn</p>
          <p>{formatMoney(totalWithdrawn, {cents: true})}</p>
        </Box>
        <Box align="right">
          <p className={styles.label}>repaid</p>
          <CentsMoney value={totalRepaid} />
        </Box>
        <Box align="right">
          <p className={styles.label}>balance</p>
          <Space size={6}>
            <MoneyBanknotesRotatedSecondIcon className={styles.greenLight} />
            <CentsMoney value={totalBalance} size="xl" />
          </Space>
        </Box>
      </Block>
    </>
  )
}

const getMonthSchedules = (transactions: any): MonthSchedule[] =>
  transactions.reduce((arr: MonthSchedule[], item: any) => {
    const date = parseISO(item.date)
    const month = getMonth(date)
    const year = getYear(date)
    const monthSchedule = arr.find(
      (arrItem) => arrItem.month === month && arrItem.year === year
    )
    if (monthSchedule) {
      monthSchedule.items.push(item)
    } else {
      arr.push({
        month,
        year,
        items: [item],
      })
    }
    return arr
  }, [])
